import http from './api'
import { FutureChampionEvent, FutureChampionPrediction } from '../types/futureChampion'

class FutureChampionService {
  // ========== EVENT METHODS ==========

  async getEvents(filters?: {
    gameType?: 'football' | 'formula1'
    isActive?: boolean
    /** When false, return all active events (including past); when true (default), only next 10 days */
    upcomingOnly?: boolean
  }): Promise<FutureChampionEvent[]> {
    const params = new URLSearchParams()
    if (filters?.gameType) params.append('gameType', filters.gameType)
    if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString())
    if (filters?.upcomingOnly === false) params.append('upcomingOnly', 'false')

    const queryString = params.toString()
    const response = await http.get(`/future-champion/events${queryString ? `?${queryString}` : ''}`)
    const eventsData = response.data?.events || response.data
    return Array.isArray(eventsData) ? eventsData : []
  }

  async getEvent(eventId: string): Promise<FutureChampionEvent> {
    const response = await http.get(`/future-champion/events/${eventId}`)
    return response.data
  }

  async createEvent(data: {
    name: string
    gameType: 'football' | 'formula1'
    eventDate: string
    choices: string[]
    isActive?: boolean
  }): Promise<FutureChampionEvent> {
    const response = await http.post('/future-champion/events', data)
    return response.data
  }

  async updateEvent(eventId: string, data: {
    name?: string
    gameType?: 'football' | 'formula1'
    eventDate?: string
    choices?: string[]
    isActive?: boolean
    actualChampion?: string
  }): Promise<FutureChampionEvent> {
    const response = await http.patch(`/future-champion/events/${eventId}`, data)
    return response.data
  }

  async deleteEvent(eventId: string): Promise<void> {
    await http.delete(`/future-champion/events/${eventId}`)
  }

  // ========== PREDICTION METHODS ==========

  async createPrediction(data: {
    roomId: string
    eventId: string
    predictedChampion: string
  }): Promise<FutureChampionPrediction> {
    const response = await http.post('/future-champion/predictions', data)
    return response.data
  }

  async getPrediction(roomId: string, eventId: string): Promise<FutureChampionPrediction | null> {
    try {
      const response = await http.get(`/future-champion/predictions/room/${roomId}/event/${eventId}`)
      return response.data?.prediction || null
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  }

  async getRoomPredictions(roomId: string, eventId?: string): Promise<FutureChampionPrediction[]> {
    const params = new URLSearchParams()
    if (eventId) params.append('eventId', eventId)

    const queryString = params.toString()
    const response = await http.get(`/future-champion/predictions/room/${roomId}${queryString ? `?${queryString}` : ''}`)
    const predictionsData = response.data?.predictions || response.data
    return Array.isArray(predictionsData) ? predictionsData : []
  }

  async getPlayerPredictions(roomId: string, playerId: string): Promise<FutureChampionPrediction[]> {
    const response = await http.get(`/future-champion/predictions/room/${roomId}/player/${playerId}`)
    const predictionsData = response.data?.predictions || response.data
    return Array.isArray(predictionsData) ? predictionsData : []
  }
}

const futureChampionService = new FutureChampionService()
export default futureChampionService
